import {type ReactHTMLElement, useEffect, useMemo, useRef, useState} from 'react';
import ForceGraph2D, {type ForceGraphMethods} from 'react-force-graph-2d';
import {
  BoxSelect,
  Info,
  Maximize2,
  Minimize2,
  Scan,
  Search,
  User,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  Slider,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {useFollowerGraph} from '@/api/hooks';
import type {Item} from '@/api/items';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {pinterestProfileUrl} from '@/lib/pinterest';
import {textTooltip} from '@/lib/tooltipLabel';
import {cn} from '@/lib/utils';

const muted = (percent: number) =>
  `color-mix(in srgb, var(--muted-foreground) ${percent}%, transparent)`;

// Categories take slots of the validated chart-cat ramp, so they follow the mode and stay apart for
// colour-blind analysts. Degree 0 is the investigated profile.
const PALETTE = {
  profile: 'var(--chart-cat-8)',
  follower: 'var(--chart-cat-1)',
  followerOfFollower: 'var(--chart-cat-other)',
  classified: 'var(--chart-cat-3)',
  mutual: 'var(--chart-cat-4)',
  dismissed: muted(40),
  dimmedNode: muted(15),
  link: muted(35),
  linkDimmed: muted(5),
  linkFocused: 'var(--foreground)',
  areaSelected: 'var(--primary)'
};
type Palette = typeof PALETTE;
const DEGREE_KEYS = ['profile', 'follower', 'followerOfFollower'] as const;
const CLASSIFIED_STATUSES = new Set(['in hopper', 'sent to client']);

/**
 The palette as colours a canvas can parse. A canvas can't read CSS variables, so each token is
 resolved through a probe element, and again whenever the mode or accent on <html> changes.
 */
function useResolvedPalette() {
  const [resolved, setResolved] = useState<Palette>(PALETTE);
  useEffect(() => {
    const resolve = () => {
      const probe = document.createElement('span');
      probe.style.display = 'none';
      document.body.append(probe);
      const next = {...PALETTE};
      for (const key of Object.keys(PALETTE) as Array<keyof Palette>) {
        probe.style.color = PALETTE[key];
        // Empty where nothing computes styles (jsdom), so the raw value is kept.
        next[key] = getComputedStyle(probe).color || PALETTE[key];
      }

      probe.remove();
      setResolved(next);
    };

    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, {attributeFilter: ['class', 'data-accent']});
    return () => {
      observer.disconnect();
    };
  }, []);

  return resolved;
}

type GraphNode = {id: string; item: Item; degree: 0 | 1 | 2; x?: number; y?: number};
type GraphLink = {source: string | GraphNode; target: string | GraphNode};

const isClassified = (item: Item) =>
  item.submission_status !== null && CLASSIFIED_STATUSES.has(item.submission_status);

const isTriaged = (item: Item) => item.item_status === 'dismissed' || isClassified(item);

const nodeColour = (node: GraphNode, palette: Palette) => {
  if (node.item.item_status === 'dismissed') {
    return palette.dismissed;
  }

  return isClassified(node.item) ? palette.classified : palette[DEGREE_KEYS[node.degree]];
};

// Node area, which is what nodeVal means to the library. The investigated profile is always the
// biggest; everyone else grows with how many follows they have inside the network.
const nodeValue = (degree: 0 | 1 | 2, connectionCount: number) =>
  degree === 0 ? 40 : 1 + connectionCount * 0.15;

/** The highest minimum-links value that still leaves a profile besides the anchor - the slider's top. */
function strongestCore(nodes: GraphNode[], links: GraphLink[]) {
  let minLinks = 1;
  while (pruneLooseEnds(nodes, links, minLinks + 1).nodes.some((n) => n.degree !== 0)) {
    minLinks += 1;
  }

  return minLinks;
}

const DEGREE_LABELS = ['Investigated profile', 'Follower', 'Follower of follower'] as const;
const NODE_REL_SIZE = 4;
const DENSE_GRAPH_NODES = 4000;
const MAX_FIT_ZOOM = 2.5;

const nodeLabel = (item: Item) => item.author_name ?? item.author_handle ?? item.external_id;

const endpointId = (end: string | GraphNode) => (typeof end === 'string' ? end : end.id);

const edgeKey = (from: string, to: string) => `${from}|${to}`;

/**
 Drops the profiles with fewer than `minLinks` links to other visible profiles, and keeps dropping
 until every one left has at least that many - the interconnected core, without the chains of
 one-off followers hanging off it. A link counts once whichever way it points. The investigated
 profile is never dropped, so the view always has its anchor.
 */
function pruneLooseEnds(nodes: GraphNode[], links: GraphLink[], minLinks: number) {
  let keptNodes = nodes;
  let keptLinks = links;
  for (;;) {
    const neighbourCount = new Map<string, Set<string>>();
    for (const {source, target} of keptLinks) {
      const [from, to] = [endpointId(source), endpointId(target)];
      neighbourCount.set(from, (neighbourCount.get(from) ?? new Set<string>()).add(to));
      neighbourCount.set(to, (neighbourCount.get(to) ?? new Set<string>()).add(from));
    }

    const next = keptNodes.filter(
      (n) => n.degree === 0 || (neighbourCount.get(n.id)?.size ?? 0) >= minLinks
    );
    if (next.length === keptNodes.length) {
      return {nodes: keptNodes, links: keptLinks};
    }

    const ids = new Set(next.map((n) => n.id));
    keptNodes = next;
    keptLinks = keptLinks.filter(
      (l) => ids.has(endpointId(l.source)) && ids.has(endpointId(l.target))
    );
  }
}

/** Who follows whom for a follower investigation; clicking a profile opens it for classification. */
export function FollowerNetworkGraph({
  jobId,
  onClassify
}: {
  readonly jobId: string;
  readonly onClassify: (items: Item[]) => void;
}) {
  const {data, isLoading, isError} = useFollowerGraph(jobId);
  const palette = useResolvedPalette();
  const graphRef = useRef<ForceGraphMethods | undefined>(undefined);
  // State rather than a ref, so measuring starts when the container mounts - it doesn't exist while
  // the loading spinner is showing, which left the canvas stuck at its initial size.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({width: 800, height: 600});
  const [search, setSearch] = useState('');
  // Profiles linked to fewer than this many others are hidden, repeatedly; 1 hides nobody.
  const [minLinks, setMinLinks] = useState(1);
  const [showSecondDegree, setShowSecondDegree] = useState(true);
  const [hideTriaged, setHideTriaged] = useState(false);
  const [mutualOnly, setMutualOnly] = useState(false);
  // The profile card shown beside a hovered node. It lingers briefly after the pointer leaves the
  // node so the pointer can travel onto the card and reach its link.
  const [card, setCard] = useState<{id: string; x: number; y: number} | null>(null);
  const cardTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [spacing, setSpacing] = useState(1);
  // The clicked profile, whose actions panel is showing, and the profile whose own network the
  // graph is narrowed to ("Show network").
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [egoId, setEgoId] = useState<string | null>(null);
  // Profiles picked by dragging a box over the graph, and whether a plain drag draws that box (it
  // otherwise pans; holding Shift draws it either way).
  const [areaIds, setAreaIds] = useState<Set<string>>(new Set());
  const [areaSelectMode, setAreaSelectMode] = useState(false);
  // Fills the browser window instead of the page's content area. Not the browser's own full screen,
  // whose top layer would hide the classify dialog and tooltips (they render outside this element).
  const [expanded, setExpanded] = useState(false);
  const [marquee, setMarquee] = useState<{x0: number; y0: number; x1: number; y1: number} | null>(
    null
  );
  const fitPending = useRef(true);

  useEffect(() => {
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setSize({width: entry.contentRect.width, height: entry.contentRect.height});
      }
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [container]);

  // Who follows whom, per profile. "Connections" is what "important" means here: a profile followed
  // by, or following, many of the others. Neighbours are taken from the whole network, not the
  // filtered view, so "Show network" and "Classify network" mean the same whatever is filtered out.
  const {connections, followers, following, neighbours} = useMemo(() => {
    const followerCounts = new Map<string, number>();
    const followingCounts = new Map<string, number>();
    const adjacent = new Map<string, Set<string>>();
    const link = (from: string, to: string) => {
      adjacent.set(from, (adjacent.get(from) ?? new Set<string>()).add(to));
    };

    for (const {follower_item_id: from, followed_item_id: to} of data?.edges ?? []) {
      followingCounts.set(from, (followingCounts.get(from) ?? 0) + 1);
      followerCounts.set(to, (followerCounts.get(to) ?? 0) + 1);
      link(from, to);
      link(to, from);
    }

    const total = new Map<string, number>();
    for (const id of adjacent.keys()) {
      total.set(id, (followerCounts.get(id) ?? 0) + (followingCounts.get(id) ?? 0));
    }

    return {
      connections: total,
      followers: followerCounts,
      following: followingCounts,
      neighbours: adjacent
    };
  }, [data]);

  // The follows that go both ways (A follows B and B follows A) - the pairs that follow each other.
  const mutualKeys = useMemo(() => {
    const all = new Set(
      (data?.edges ?? []).map((e) => edgeKey(e.follower_item_id, e.followed_item_id))
    );
    return new Set(
      (data?.edges ?? [])
        .filter((e) => all.has(edgeKey(e.followed_item_id, e.follower_item_id)))
        .map((e) => edgeKey(e.follower_item_id, e.followed_item_id))
    );
  }, [data]);

  const maxMinLinks = useMemo(
    () =>
      strongestCore(
        (data?.nodes ?? []).map((n): GraphNode => ({
          id: n.item.id,
          item: n.item,
          degree: n.degree
        })),
        (data?.edges ?? []).map((e): GraphLink => ({
          source: e.follower_item_id,
          target: e.followed_item_id
        }))
      ),
    [data]
  );

  // The graph library mutates the objects it is given, so hand it a copy it can own.
  const graphData = useMemo(() => {
    // Narrowed to one profile and the ones directly linked to it, the degree and connection filters
    // would only hide the very network being looked at, so just "hide classified" still applies.
    const egoSet = egoId ? new Set([egoId, ...(neighbours.get(egoId) ?? [])]) : null;
    const visible = (data?.nodes ?? []).filter((n) => {
      if (egoSet) {
        return egoSet.has(n.item.id) && !(hideTriaged && isTriaged(n.item) && n.item.id !== egoId);
      }

      return (
        n.degree === 0 ||
        ((showSecondDegree || n.degree < 2) && !(hideTriaged && isTriaged(n.item)))
      );
    });
    const ids = new Set(visible.map((n) => n.item.id));
    let nodes = visible.map((n): GraphNode => ({id: n.item.id, item: n.item, degree: n.degree}));
    const links = (data?.edges ?? [])
      .filter((e) => ids.has(e.follower_item_id) && ids.has(e.followed_item_id))
      .filter((e) => !mutualOnly || mutualKeys.has(edgeKey(e.follower_item_id, e.followed_item_id)))
      .map((e): GraphLink => ({source: e.follower_item_id, target: e.followed_item_id}));

    if (mutualOnly) {
      // Only profiles that take part in a mutual follow stay (and the investigated one, as the anchor).
      const linked = new Set(links.flatMap((l) => [endpointId(l.source), endpointId(l.target)]));
      nodes = nodes.filter((n) => n.degree === 0 || linked.has(n.id));
    }

    return minLinks > 1 ? pruneLooseEnds(nodes, links, minLinks) : {nodes, links};
  }, [data, neighbours, mutualKeys, egoId, minLinks, showSecondDegree, hideTriaged, mutualOnly]);

  useEffect(() => {
    fitPending.current = true;
  }, [graphData]);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return null;
    }

    return new Set(
      graphData.nodes
        .filter((n) =>
          [n.item.author_name, n.item.author_handle, n.item.external_id].some((value) =>
            value?.toLowerCase().includes(term)
          )
        )
        .map((n) => n.id)
    );
  }, [search, graphData]);

  // What stays at full strength: the hovered (else clicked) profile and the ones it is linked to,
  // otherwise the search matches. null means nothing is dimmed.
  const focus = useMemo(() => {
    const id = card?.id ?? selectedId;
    if (id) {
      return new Set([id, ...(neighbours.get(id) ?? [])]);
    }

    return areaIds.size > 0 ? areaIds : matches;
  }, [card, selectedId, areaIds, matches, neighbours]);

  // Only what is actually on screen counts: a filter can hide profiles that were boxed earlier, and
  // those must not be classified unseen.
  const areaNodes = useMemo(
    () => graphData.nodes.filter((n) => areaIds.has(n.id)),
    [graphData, areaIds]
  );
  const areaToClassify = useMemo(
    () => areaNodes.filter((n) => !isTriaged(n.item)).map((n) => n.item),
    [areaNodes]
  );

  const selectedNode = selectedId ? data?.nodes.find((n) => n.item.id === selectedId) : undefined;
  // Everyone linked to the selected profile who has not already been classified or dismissed, plus
  // the profile itself if it hasn't - what "Classify network" would open.
  const networkToClassify = useMemo(() => {
    if (!selectedNode || !data) {
      return [];
    }

    const ids = new Set([selectedNode.item.id, ...(neighbours.get(selectedNode.item.id) ?? [])]);
    return data.nodes.filter((n) => ids.has(n.item.id) && !isTriaged(n.item)).map((n) => n.item);
  }, [selectedNode, data, neighbours]);
  const hoverNode = card ? data?.nodes.find((n) => n.item.id === card.id) : undefined;
  const egoNode = egoId ? data?.nodes.find((n) => n.item.id === egoId) : undefined;

  // Repulsion and link length grow with node size, so a few big hubs left by a filter are spread
  // out rather than piled on top of each other. The spacing slider multiplies both.
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) {
      return;
    }

    const valueOf = (end: string | GraphNode) =>
      typeof end === 'string' ? 1 : nodeValue(end.degree, connections.get(end.id) ?? 0);
    // D3Force is typed any by the library.
    const charge = graph.d3Force('charge') as
      {strength: (fn: (node: GraphNode) => number) => unknown} | undefined;
    const link = graph.d3Force('link') as
      {distance: (fn: (link: GraphLink) => number) => unknown} | undefined;
    charge?.strength(
      (node) => -(30 + nodeValue(node.degree, connections.get(node.id) ?? 0) * 40) * spacing
    );
    link?.distance(
      (l) =>
        (25 + NODE_REL_SIZE * (Math.sqrt(valueOf(l.source)) + Math.sqrt(valueOf(l.target)))) *
        spacing
    );
    graph.d3ReheatSimulation();
  }, [graphData, connections, spacing]);

  useEffect(
    () => () => {
      clearTimeout(cardTimer.current);
    },
    []
  );

  const hideCardSoon = () => {
    clearTimeout(cardTimer.current);
    cardTimer.current = setTimeout(() => {
      setCard(null);
    }, 250);
  };

  // Escape steps back: first the box selection, then the expanded view.
  useEffect(() => {
    if (areaIds.size === 0 && !expanded) {
      return;
    }

    const stepBack = (e: KeyboardEvent) => {
      // An Escape that a dialog (the classify dialog opened from here) has already used to close
      // itself must not also undo the selection behind it - one Escape undoes one thing.
      if (
        e.key !== 'Escape' ||
        e.defaultPrevented ||
        (e.target instanceof Element && e.target.closest('[role="dialog"]'))
      ) {
        return;
      }

      if (areaIds.size > 0) {
        setAreaIds(new Set());
      } else {
        setExpanded(false);
      }
    };

    globalThis.addEventListener('keydown', stepBack);
    return () => {
      globalThis.removeEventListener('keydown', stepBack);
    };
  }, [areaIds, expanded]);

  // Dragging draws a box and selects every profile whose dot is inside it. Captured before the graph
  // sees the press (which would pan), and only when select mode is on or Shift is held. Ctrl/Cmd
  // adds to the current selection instead of replacing it; a click without a drag clears it.
  const startMarquee = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      e.button !== 0 ||
      !(areaSelectMode || e.shiftKey) ||
      (e.target as HTMLElement).closest('[data-no-marquee]')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const element = e.currentTarget;
    const bounds = element.getBoundingClientRect();
    const local = (at: {clientX: number; clientY: number}) => ({
      x: at.clientX - bounds.left - element.clientLeft,
      y: at.clientY - bounds.top - element.clientTop
    });
    const from = local(e);
    const additive = e.ctrlKey || e.metaKey;
    setCard(null);

    const move = (ev: MouseEvent) => {
      const to = local(ev);
      setMarquee({x0: from.x, y0: from.y, x1: to.x, y1: to.y});
    };

    const up = (ev: MouseEvent) => {
      globalThis.removeEventListener('mousemove', move);
      globalThis.removeEventListener('mouseup', up);
      setMarquee(null);
      const to = local(ev);
      const [left, right] = [Math.min(from.x, to.x), Math.max(from.x, to.x)];
      const [top, bottom] = [Math.min(from.y, to.y), Math.max(from.y, to.y)];
      if (right - left < 4 && bottom - top < 4) {
        if (!additive) {
          setAreaIds(new Set());
        }

        return;
      }

      const inside = graphData.nodes
        .filter((n) => {
          const at =
            n.x === undefined || n.y === undefined
              ? undefined
              : graphRef.current?.graph2ScreenCoords(n.x, n.y);
          return at !== undefined && at.x >= left && at.x <= right && at.y >= top && at.y <= bottom;
        })
        .map((n) => n.id);
      setAreaIds((previous) => new Set([...(additive ? previous : []), ...inside]));
    };

    globalThis.addEventListener('mousemove', move);
    globalThis.addEventListener('mouseup', up);
  };

  const zoomBy = (factor: number) => {
    const graph = graphRef.current;
    graph?.zoom(graph.zoom() * factor, 300);
  };

  const fitView = () => {
    graphRef.current?.zoomToFit(400, 60);
    // A filter can leave a handful of nodes, which fit to an enormous zoom. Checked once the fit
    // animation has finished, since zoom() reports the level mid-animation until then.
    setTimeout(() => {
      const graph = graphRef.current;
      if (graph && graph.zoom() > MAX_FIT_ZOOM) {
        graph.zoom(MAX_FIT_ZOOM, 300);
      }
    }, 450);
  };

  const jumpToFirstMatch = () => {
    const target = graphData.nodes.find((n) => matches?.has(n.id) && n.x !== undefined);
    if (target?.x !== undefined && target.y !== undefined) {
      graphRef.current?.centerAt(target.x, target.y, 600);
      graphRef.current?.zoom(6, 600);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-muted-foreground p-8 text-xs">Couldn&apos;t load the follower network.</p>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        expanded ? 'bg-card fixed inset-0 z-40 p-4' : 'h-full min-h-[560px]'
      )}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <div className="relative w-56">
          <Search className="text-muted-foreground absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            value={search}
            placeholder="Find a profile…"
            className="h-8 pl-7 text-xs"
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                jumpToFirstMatch();
              }
            }}
          />
        </div>
        <Field orientation="horizontal" className="w-auto gap-2">
          <FieldLabel className="whitespace-nowrap">
            Min links per profile: {minLinks > 1 ? minLinks : 'any'}
          </FieldLabel>
          <div className="w-32 shrink-0">
            <Slider
              min={1}
              max={Math.max(2, maxMinLinks)}
              step={1}
              value={minLinks}
              getAriaLabel={() => 'Min links per profile'}
              onValueChange={(next) => {
                const level = typeof next === 'number' ? next : next[0];
                if (typeof level === 'number') {
                  setMinLinks(level);
                }
              }}
            />
          </div>
          <HelpTip>
            Hides profiles linked to fewer than this many others, then repeats until everyone left
            has at least that many. Raise it to strip away one-off followers and keep only the
            tightly interconnected core. A link counts once whichever way it points; the
            investigated profile always stays.
          </HelpTip>
        </Field>
        <Field orientation="horizontal" className="w-auto gap-2">
          <FieldLabel className="whitespace-nowrap">Spacing: {spacing.toFixed(1)}×</FieldLabel>
          <div className="w-28 shrink-0">
            <Slider
              min={0.5}
              max={6}
              step={0.5}
              value={spacing}
              getAriaLabel={() => 'Spacing'}
              onValueChange={(next) => {
                const level = typeof next === 'number' ? next : next[0];
                if (typeof level === 'number') {
                  setSpacing(level);
                }
              }}
            />
          </div>
          <HelpTip>
            Spreads the profiles further apart or pulls them closer together. It doesn&apos;t change
            which profiles are shown.
          </HelpTip>
        </Field>
        <label className="flex cursor-pointer items-center gap-1.5 font-medium">
          <Checkbox
            checked={showSecondDegree}
            onCheckedChange={(checked: boolean) => {
              setShowSecondDegree(checked);
            }}
          />
          Followers of followers
          <HelpTip>
            The second hop: people who follow the investigated profile&apos;s followers. Untick to
            see just the profile and its own followers.
          </HelpTip>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 font-medium">
          <Checkbox
            checked={mutualOnly}
            onCheckedChange={(checked: boolean) => {
              setMutualOnly(checked);
            }}
          />
          Mutual follows only
          <HelpTip>
            Shows only follows that go both ways - two profiles that each follow the other - and the
            profiles involved, drawn in amber. Only the investigated profile&apos;s followers can be
            mutual, because they are the only ones whose own followers we collect.
          </HelpTip>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 font-medium">
          <Checkbox
            checked={hideTriaged}
            onCheckedChange={(checked: boolean) => {
              setHideTriaged(checked);
            }}
          />
          Hide classified &amp; dismissed
          <HelpTip>
            Hides profiles that have already been classified or dismissed, so only the ones still
            needing a decision are left.
          </HelpTip>
        </label>
      </div>
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          {graphData.nodes.length} of {data.nodes.length} profiles · {graphData.links.length}{' '}
          follows · bigger means more follows in the network · Shift-drag to select an area
        </span>
        {egoNode ? (
          <span className="text-foreground flex items-center gap-2 font-medium">
            Network of {nodeLabel(egoNode.item)}
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                setEgoId(null);
              }}>
              Show full network
            </Button>
          </span>
        ) : null}
        {data.pending_child_jobs > 0 ? (
          <span>Still collecting followers of followers ({data.pending_child_jobs} running)</span>
        ) : null}
        {data.expanded_followers > 0 && data.expanded_followers < data.first_degree_followers ? (
          <span>
            Followers&apos; followers collected for {data.expanded_followers} of{' '}
            {data.first_degree_followers} followers
          </span>
        ) : null}
        {data.truncated ? (
          <span>Showing the first profiles only — network is too large.</span>
        ) : null}
        <span className="ml-auto flex items-center gap-3">
          {(
            [
              ['Profile', PALETTE.profile],
              ['Follower', PALETTE.follower],
              ['Follower of follower', PALETTE.followerOfFollower],
              ['Classified', PALETTE.classified],
              ['Dismissed', PALETTE.dismissed],
              ['Mutual follow', PALETTE.mutual]
            ] as const
          ).map(([label, colour]) => (
            <span key={label} className="flex items-center gap-1">
              <span className="inline-block size-2" style={{background: colour}} />
              {label}
            </span>
          ))}
        </span>
      </div>
      <div
        ref={setContainer}
        data-testid="graph-area"
        className={cn(
          'border-border relative min-h-0 flex-1 overflow-hidden rounded-xl border',
          areaSelectMode && '[&_canvas]:cursor-crosshair'
        )}
        onMouseDownCapture={startMarquee}>
        <ForceGraph2D
          ref={graphRef}
          width={size.width}
          height={size.height}
          graphData={graphData}
          nodeId="id"
          nodeRelSize={NODE_REL_SIZE}
          // Size by connections, so the profiles worth a look stand out; the investigated one is
          // always the biggest.
          nodeVal={(node) => {
            const {id, degree} = node as GraphNode;
            return nodeValue(degree, connections.get(id) ?? 0);
          }}
          nodeColor={(node) => {
            const n = node as GraphNode;
            return focus && !focus.has(n.id) ? palette.dimmedNode : nodeColour(n, palette);
          }}
          // A string label goes into the tooltip as innerHTML, and this one is a scraped channel name,
          // so it is passed as an element. The prop's type says React element; float-tooltip takes a DOM one.
          nodeLabel={(node) =>
            textTooltip(
              nodeLabel((node as GraphNode).item)
            ) as unknown as ReactHTMLElement<HTMLElement>
          }
          // Thousands of arrowheads are what makes a big network slow to draw, so they are dropped for one
          // until a profile is hovered or selected, when its own links need to show their direction.
          linkDirectionalArrowLength={
            focus ? 5 : graphData.nodes.length > DENSE_GRAPH_NODES ? 0 : 3
          }
          linkDirectionalArrowRelPos={1}
          linkWidth={(link) => {
            const {source, target} = link as GraphLink;
            if (focus?.has(endpointId(source)) && focus.has(endpointId(target))) {
              return 1.5;
            }

            return mutualKeys.has(edgeKey(endpointId(source), endpointId(target))) ? 1.2 : 0.5;
          }}
          linkColor={(link) => {
            const {source, target} = link as GraphLink;
            const [from, to] = [endpointId(source), endpointId(target)];
            if (focus && !(focus.has(from) && focus.has(to))) {
              return palette.linkDimmed;
            }

            if (focus) {
              return palette.linkFocused;
            }

            return mutualKeys.has(edgeKey(from, to)) ? palette.mutual : palette.link;
          }}
          nodeCanvasObjectMode={() => 'after'}
          nodeCanvasObject={(node, ctx, scale) => {
            const {id, degree, x, y} = node as GraphNode;
            if (!areaIds.has(id) || x === undefined || y === undefined) {
              return;
            }

            // A ring just outside the dot, kept the same thickness however far in or out you are.
            ctx.beginPath();
            ctx.arc(
              x,
              y,
              Math.sqrt(nodeValue(degree, connections.get(id) ?? 0)) * NODE_REL_SIZE + 2 / scale,
              0,
              2 * Math.PI
            );
            ctx.lineWidth = 2 / scale;
            ctx.strokeStyle = palette.areaSelected;
            ctx.stroke();
          }}
          cooldownTime={8000}
          onEngineStop={() => {
            if (fitPending.current) {
              fitPending.current = false;
              fitView();
            }
          }}
          onNodeHover={(node) => {
            if (!node) {
              hideCardSoon();
              return;
            }

            const {id, x, y} = node as GraphNode;
            clearTimeout(cardTimer.current);
            const at = graphRef.current?.graph2ScreenCoords(x ?? 0, y ?? 0);
            setCard({id, x: at?.x ?? 0, y: at?.y ?? 0});
          }}
          onNodeClick={(node) => {
            setSelectedId((node as GraphNode).id);
          }}
          onBackgroundClick={() => {
            setSelectedId(null);
          }}
        />
        {hoverNode && card ? (
          <div
            data-no-marquee
            className="bg-card border-border absolute z-20 w-60 space-y-2 rounded-lg border p-3 text-xs shadow-lg"
            style={{
              left: Math.max(8, Math.min(card.x + 14, size.width - 248)),
              top: Math.max(8, Math.min(card.y + 14, size.height - 140))
            }}
            onMouseEnter={() => {
              clearTimeout(cardTimer.current);
            }}
            onMouseLeave={hideCardSoon}>
            <div className="flex items-start gap-2.5">
              <ProfileAvatar item={hoverNode.item} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{nodeLabel(hoverNode.item)}</p>
                {hoverNode.item.author_handle &&
                hoverNode.item.author_handle !== nodeLabel(hoverNode.item) ? (
                  <p className="text-muted-foreground truncate">@{hoverNode.item.author_handle}</p>
                ) : null}
                <p className="text-muted-foreground mt-1">
                  {DEGREE_LABELS[hoverNode.degree]} · followed by{' '}
                  {followers.get(hoverNode.item.id) ?? 0} · follows{' '}
                  {following.get(hoverNode.item.id) ?? 0}
                </p>
              </div>
            </div>
            <PinterestLink item={hoverNode.item} />
          </div>
        ) : null}
        {selectedNode ? (
          <div
            data-no-marquee
            className="bg-card border-border absolute top-3 left-3 z-10 w-72 space-y-3 rounded-xl border p-3 text-xs shadow-md">
            <div className="flex items-start gap-2">
              <ProfileAvatar item={selectedNode.item} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{nodeLabel(selectedNode.item)}</p>
                {selectedNode.item.author_handle &&
                selectedNode.item.author_handle !== nodeLabel(selectedNode.item) ? (
                  <p className="text-muted-foreground truncate">
                    @{selectedNode.item.author_handle}
                  </p>
                ) : null}
                <p className="text-muted-foreground mt-1">
                  {DEGREE_LABELS[selectedNode.degree]} · followed by{' '}
                  {followers.get(selectedNode.item.id) ?? 0} · follows{' '}
                  {following.get(selectedNode.item.id) ?? 0} in this network
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSelectedId(null);
                }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col gap-1.5">
              <PinterestLink item={selectedNode.item} />
              {egoId === selectedNode.item.id ? null : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEgoId(selectedNode.item.id);
                  }}>
                  Show network
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => {
                  onClassify([selectedNode.item]);
                }}>
                Classify profile
              </Button>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={networkToClassify.length === 0}
                      onClick={() => {
                        onClassify(networkToClassify);
                      }}
                    />
                  }>
                  Classify network ({networkToClassify.length})
                </TooltipTrigger>
                <TooltipContent>
                  This profile and everyone linked to it, except those already classified or
                  dismissed
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : null}
        <div
          data-no-marquee
          className="bg-card border-border absolute top-3 right-3 flex flex-col overflow-hidden rounded-lg border shadow-sm">
          <ZoomButton
            label="Select an area (drag a box, or hold Shift and drag)"
            active={areaSelectMode}
            onClick={() => {
              setAreaSelectMode((on) => !on);
            }}>
            <BoxSelect className="h-4 w-4" />
          </ZoomButton>
          <ZoomButton
            label="Zoom in"
            onClick={() => {
              zoomBy(1.5);
            }}>
            <ZoomIn className="h-4 w-4" />
          </ZoomButton>
          <ZoomButton
            label="Zoom out"
            onClick={() => {
              zoomBy(1 / 1.5);
            }}>
            <ZoomOut className="h-4 w-4" />
          </ZoomButton>
          <ZoomButton label="Fit to view" onClick={fitView}>
            <Scan className="h-4 w-4" />
          </ZoomButton>
          <ZoomButton
            label={expanded ? 'Exit full window' : 'Fill the window'}
            active={expanded}
            onClick={() => {
              setExpanded((on) => !on);
            }}>
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </ZoomButton>
        </div>
        {marquee ? (
          <div
            className="border-primary bg-primary/10 pointer-events-none absolute z-30 border"
            style={{
              left: Math.min(marquee.x0, marquee.x1),
              top: Math.min(marquee.y0, marquee.y1),
              width: Math.abs(marquee.x1 - marquee.x0),
              height: Math.abs(marquee.y1 - marquee.y0)
            }}
          />
        ) : null}
        {areaNodes.length > 0 ? (
          <div
            data-no-marquee
            className="bg-card border-border absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-xl border px-3 py-2 text-xs shadow-md">
            <span className="font-medium">{areaNodes.length} selected</span>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="sm"
                    disabled={areaToClassify.length === 0}
                    onClick={() => {
                      onClassify(areaToClassify);
                    }}
                  />
                }>
                Classify {areaToClassify.length}
              </TooltipTrigger>
              <TooltipContent>
                Classify the selected profiles together, except those already classified or
                dismissed
              </TooltipContent>
            </Tooltip>
            {areaToClassify.length < areaNodes.length ? (
              <span className="text-muted-foreground">
                {areaNodes.length - areaToClassify.length} already classified or dismissed
              </span>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setAreaIds(new Set());
              }}>
              Clear
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ZoomButton({
  label,
  onClick,
  active = false,
  children
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly active?: boolean;
  readonly children: React.ReactNode;
}) {
  // The pressed state edges in primary rather than relying on bg-accent alone, which is a
  // 1.03:1 shift against the card nobody can see.
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            aria-pressed={active}
            className={cn(
              'text-muted-foreground',
              active && 'border-primary bg-primary/5 text-foreground'
            )}
            onClick={onClick}
          />
        }>
        {children}
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

function PinterestLink({item}: {readonly item: Item}) {
  const href = pinterestProfileUrl(item);
  return href ? <CopyableUrl url={href} label="Copy profile URL" /> : null;
}

/**
 The profile's picture. It is scraped content, so it follows the analyst's thumbnail-blur setting
 like every other picture of a profile in the app, and only https images are loaded at all.
 */
function ProfileAvatar({item}: {readonly item: Item}) {
  const src = item.thumbnail_url?.startsWith('https://') ? item.thumbnail_url : undefined;
  return (
    <Avatar size="lg" className="group">
      {src ? (
        <SensitiveMedia
          compact
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          className="aspect-square size-full rounded-full object-cover"
        />
      ) : (
        <AvatarFallback variant="primary">
          <User className="h-4 w-4" />
        </AvatarFallback>
      )}
    </Avatar>
  );
}

/** A small (i) that explains a control when hovered or focused. */
function HelpTip({children}: {readonly children: React.ReactNode}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="What does this do?"
            className="text-muted-foreground"
            onClick={(e) => {
              // Inside a label, a click would otherwise toggle the checkbox or focus the slider.
              e.preventDefault();
            }}
          />
        }>
        <Info className="h-3.5 w-3.5" />
      </TooltipTrigger>
      <TooltipContent className="max-w-72 text-pretty">{children}</TooltipContent>
    </Tooltip>
  );
}
